import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { TrendingUp, ShieldCheck, BarChart3, Sparkles } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';

const AuthPage = () => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const { branding } = useBranding();

  const toggleMode = () => setIsLoginMode(!isLoginMode);

  const primary = branding.primary_color || '#1f2937';
  const secondary = branding.secondary_color || '#3b82f6';
  const accent = branding.accent_color || '#10b981';

  const features = [
    {
      icon: BarChart3,
      title: 'Financial Modeling',
      desc: 'Real-time P&L, Balance Sheet & Cash Flow',
      color: secondary,
    },
    {
      icon: TrendingUp,
      title: 'Scenario Planning',
      desc: 'Base, best and worst case analysis',
      color: accent,
    },
    {
      icon: ShieldCheck,
      title: 'Enterprise Ready',
      desc: 'Bank-grade security & collaboration',
      color: secondary,
    },
  ];

  // Branding override: custom login background image replaces the showcase panel art
  const showcaseStyle = branding.custom_login_background
    ? {
        backgroundImage: `linear-gradient(135deg, ${primary}f2 0%, ${primary}cc 100%), url(${branding.custom_login_background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background: `linear-gradient(150deg, ${primary} 0%, ${primary} 55%, #0b1220 100%)`,
      };

  return (
    <div
      className="min-h-screen w-full lg:grid lg:grid-cols-[1.05fr_1fr] bg-slate-50"
      data-testid="auth-page"
    >
      {/* ============ LEFT: Brand Showcase ============ */}
      <div
        className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 xl:p-16 text-white"
        style={showcaseStyle}
        data-testid="auth-showcase"
      >
        {/* Decorative glows */}
        <div
          className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl opacity-30"
          style={{ background: secondary }}
        />
        <div
          className="pointer-events-none absolute bottom-0 -left-20 h-80 w-80 rounded-full blur-3xl opacity-20"
          style={{ background: accent }}
        />
        {/* Subtle grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />

        {/* Logo + name */}
        <div className="relative z-10 flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="rounded-2xl bg-white/10 p-2 backdrop-blur-sm ring-1 ring-white/20">
            <img
              src={branding.logo_url || '/TavPlans_Logo.png'}
              alt={`${branding.company_name} Logo`}
              className="h-11 w-11 object-contain"
            />
          </div>
          <span className="text-2xl font-bold tracking-tight">
            {branding.company_name}
          </span>
        </div>

        {/* Headline + features */}
        <div className="relative z-10 max-w-md animate-in fade-in slide-in-from-bottom-6 duration-700">
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-white/20 backdrop-blur-sm"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <Sparkles className="h-3.5 w-3.5" style={{ color: accent }} />
            Business Planning &amp; Financial Modeling
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Plan smarter.
            <br />
            <span style={{ color: secondary }}>Forecast with confidence.</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/70">
            Build comprehensive financial models with advanced scenario analysis,
            strategic planning tools and real-time collaboration.
          </p>

          <div className="mt-9 space-y-3">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="flex items-center gap-4 rounded-2xl bg-white/[0.06] p-4 ring-1 ring-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/[0.12] hover:translate-x-1"
                style={{ animationDelay: `${150 + i * 100}ms` }}
              >
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: `${f.color}22` }}
                >
                  <f.icon className="h-5 w-5" style={{ color: f.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{f.title}</h3>
                  <p className="text-sm text-white/60">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats footer */}
        <div className="relative z-10 flex items-center gap-8 animate-in fade-in duration-1000">
          <div>
            <div className="text-2xl font-bold">10k+</div>
            <div className="text-sm text-white/60">Plans built</div>
          </div>
          <div className="h-10 w-px bg-white/15" />
          <div>
            <div className="text-2xl font-bold">99.9%</div>
            <div className="text-sm text-white/60">Uptime</div>
          </div>
          <div className="h-10 w-px bg-white/15" />
          <div>
            <div className="text-2xl font-bold">SOC 2</div>
            <div className="text-sm text-white/60">Compliant</div>
          </div>
        </div>
      </div>

      {/* ============ RIGHT: Auth Form ============ */}
      <div className="flex min-h-screen flex-col items-center justify-center px-5 py-10 sm:px-8">
        {/* Mobile logo (shown when showcase panel is hidden) */}
        <div className="mb-6 flex items-center gap-3 lg:hidden">
          <img
            src={branding.logo_url || '/TavPlans_Logo.png'}
            alt={`${branding.company_name} Logo`}
            className="h-10 w-10 object-contain"
          />
          <span className="text-xl font-bold" style={{ color: primary }}>
            {branding.company_name}
          </span>
        </div>

        <div className="w-full max-w-md space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {isLoginMode ? (
            <LoginForm onToggleMode={toggleMode} />
          ) : (
            <RegisterForm onToggleMode={toggleMode} />
          )}

          {/* Affiliate link */}
          <div
            className="flex items-center justify-center gap-2 text-center text-sm text-slate-500"
            data-testid="affiliate-cta"
          >
            <span>Interested in earning commissions?</span>
            <a
              href="/affiliate/signup"
              className="font-semibold transition-colors hover:underline"
              style={{ color: secondary }}
            >
              Join our Affiliate Program →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
